/**
 * Workspace Service
 * API wrapper for workspace management endpoints
 *
 * Endpoints reference: backend/routes/workspaces.js
 */

import axios from 'axios';
import config from '../utils/config';
import { getAuthToken } from '../utils/helpers';

const API_BASE = config.SERVER_BASE_URL;

/**
 * Get authorization headers for authenticated requests
 */
const getHeaders = () => ({
  Authorization: getAuthToken(),
  'Content-Type': 'application/json',
});

/**
 * Workspace Service API
 */
export const workspaceService = {
  // ============================================================================
  // TEAM SEARCH & AVAILABILITY
  // ============================================================================

  /**
   * Search teams from NatStat BigQuery
   * @param {string} query - Search query
   * @param {string} league - League code (optional)
   * @param {string} sport - Sport type (optional)
   * @param {number} limit - Max results (default 20)
   */
  async searchTeams(query, league = null, sport = null, limit = 20) {
    const params = new URLSearchParams({ q: query, limit: String(limit) });
    if (league) params.append('league', league);
    if (sport) params.append('sport', sport);

    const response = await axios.get(
      `${API_BASE}/api/workspaces/teams/search?${params}`,
      { headers: getHeaders() }
    );
    return response.data;
  },

  /**
   * Check if a team is available to claim (no existing workspace)
   * @param {string} teamId - Team ID
   * @param {string} league - League code
   */
  async checkTeamAvailability(teamId, league) {
    const response = await axios.get(
      `${API_BASE}/api/workspaces/teams/${teamId}/availability?league=${league}`,
      { headers: getHeaders() }
    );
    return response.data;
  },

  // ============================================================================
  // WORKSPACES
  // ============================================================================

  /**
   * List all workspaces for the current user
   */
  async listWorkspaces() {
    const response = await axios.get(
      `${API_BASE}/api/workspaces`,
      { headers: getHeaders() }
    );
    return response.data;
  },

  /**
   * Get a single workspace with staff and invitations
   * @param {string} id - Workspace ID
   */
  async getWorkspace(id) {
    const response = await axios.get(
      `${API_BASE}/api/workspaces/${id}`,
      { headers: getHeaders() }
    );
    return response.data;
  },

  /**
   * Create a new workspace (claim a team)
   * @param {Object} data - Workspace data
   * @param {string} data.team_id - Team ID from NatStat
   * @param {string} data.team_code - Team code
   * @param {string} data.team_name - Team name
   * @param {string} data.league_code - League code
   * @param {string} data.conference - Conference (optional)
   * @param {string} data.division - Division (optional)
   * @param {string} data.persona_id - Claiming user's persona
   */
  async createWorkspace(data) {
    const response = await axios.post(
      `${API_BASE}/api/workspaces`,
      data,
      { headers: getHeaders() }
    );
    return response.data;
  },

  /**
   * Sync roster from NatStat BigQuery
   * @param {string} workspaceId - Workspace ID
   */
  async syncRoster(workspaceId) {
    const response = await axios.post(
      `${API_BASE}/api/workspaces/${workspaceId}/sync-roster`,
      {},
      { headers: getHeaders() }
    );
    return response.data;
  },

  // ============================================================================
  // INVITATIONS
  // ============================================================================

  /**
   * List invitations for a workspace
   * @param {string} workspaceId - Workspace ID
   * @param {string} status - Filter by status (optional)
   */
  async listInvitations(workspaceId, status = null) {
    const params = status ? `?status=${status}` : '';
    const response = await axios.get(
      `${API_BASE}/api/workspaces/${workspaceId}/invitations${params}`,
      { headers: getHeaders() }
    );
    return response.data;
  },

  /**
   * Send a single invitation
   * @param {string} workspaceId - Workspace ID
   * @param {Object} data - Invitation data
   * @param {string} data.email - Invitee email
   * @param {string} data.persona_id - Assigned persona
   * @param {string} data.name - Invitee name (optional)
   * @param {string} data.message - Custom message (optional)
   */
  async sendInvitation(workspaceId, data) {
    const response = await axios.post(
      `${API_BASE}/api/workspaces/${workspaceId}/invitations`,
      data,
      { headers: getHeaders() }
    );
    return response.data;
  },

  /**
   * Send multiple invitations at once
   * @param {string} workspaceId - Workspace ID
   * @param {Array} invitations - Array of invitation objects
   */
  async batchInvite(workspaceId, invitations) {
    const response = await axios.post(
      `${API_BASE}/api/workspaces/${workspaceId}/invitations/batch`,
      { invitations },
      { headers: getHeaders() }
    );
    return response.data;
  },

  /**
   * Accept an invitation (public endpoint)
   * @param {string} token - Invitation token
   * @param {string} userId - Accepting user's ID
   */
  async acceptInvitation(token, userId) {
    const response = await axios.post(
      `${API_BASE}/api/invitations/${token}/accept`,
      { userId }
    );
    return response.data;
  },

  /**
   * Get invitation details by token (for acceptance page)
   * @param {string} token - Invitation token
   */
  async getInvitationByToken(token) {
    const response = await axios.get(
      `${API_BASE}/api/invitations/${token}`
    );
    return response.data;
  },

  // ============================================================================
  // PERSONAS
  // ============================================================================

  /**
   * List all persona definitions
   * @param {string} sport - Filter by sport (optional)
   * @param {string} tier - Filter by tier (optional)
   */
  async listPersonas(sport = null, tier = null) {
    const params = new URLSearchParams();
    if (sport) params.append('sport', sport);
    if (tier) params.append('tier', tier);
    const queryString = params.toString() ? `?${params}` : '';

    const response = await axios.get(
      `${API_BASE}/api/workspaces/personas${queryString}`,
      { headers: getHeaders() }
    );
    return response.data;
  },

  // ============================================================================
  // STAFF MANAGEMENT
  // ============================================================================

  /**
   * Update a staff member's role or access level
   * @param {string} workspaceId - Workspace ID
   * @param {string} staffMemberId - Staff member ID
   * @param {Object} data - Update data
   */
  async updateStaffMember(workspaceId, staffMemberId, data) {
    const response = await axios.patch(
      `${API_BASE}/api/workspaces/${workspaceId}/staff/${staffMemberId}`,
      data,
      { headers: getHeaders() }
    );
    return response.data;
  },

  /**
   * Remove a staff member from workspace
   * @param {string} workspaceId - Workspace ID
   * @param {string} staffMemberId - Staff member ID
   */
  async removeStaffMember(workspaceId, staffMemberId) {
    const response = await axios.delete(
      `${API_BASE}/api/workspaces/${workspaceId}/staff/${staffMemberId}`,
      { headers: getHeaders() }
    );
    return response.data;
  },
};

export default workspaceService;
